// A team's banner (PLAN-2026-10-24-team-banners §3): one picture behind the
// team on the screens about it — My Team's header, each side of Game Day,
// Edit team's preview — under everything drawn there.
//
// ONE PICTURE FOR ANY BOX, NEVER A PATTERN. A scene is a few layers, each
// placed rather than stretched:
//
//   `wide`   stretches across the whole box (a sky's gradient, hills, turf,
//            water): a curve or a gradient stretched wider still looks like
//            itself, and a line keeps its width (`non-scaling-stroke`);
//   `end`    a box of fixed proportions pinned to the banner's end and sized
//            by its height, so the moon, the goalpost and the light tower are
//            the same picture at any width and never squashed;
//   `start`, `mid`  the same, at the start and in the middle.
//
// THE END IS WHERE THE CHARACTER STANDS, so a matchup's other side is drawn
// mirrored and the two scenes meet behind the two characters. In the end's
// coordinates the character takes about x 72-146 from y 24 down, so nothing
// bright goes there (a moon there read as a hat); what stands behind it is
// scenery (a goalpost, a rainbow, a tower at the edge), and the bright
// things sit in the top corner or to its left.
//
// BRIGHT THINGS ARE DIMMER ON A DARK PAGE (`b-bright`, kit.css): a pale moon
// behind light digits is the one place a banner could cost a score its
// contrast.
//
// QUIET. Every shape is a middle tone at part strength over the page, so it
// reads over a light page and a dark one, and the names, the scores and the
// character drawn on it read as well as they did without it. An everyday
// scene is painted in the team's two colors, moved into the middle tones
// (`middle`); a seasonal one in its own.
//
// A FEW ELEMENTS EACH, as a face is: a row of marks of one color is one path.

import type { ReactNode } from 'react';
import {
  bannerSceneOn,
  type BannerScene,
  type TeamBadge,
  type TeamBannerId,
  type TeamColors,
} from '@fsp/domain';
import { heart, star, useDrawingId } from './FaceArt';

// ── Placing ────────────────────────────────────────────────────────────────

type Where = 'wide' | 'start' | 'mid' | 'end';

/** Each layer's own coordinates, and how they sit in the banner's box. */
const PLACES: Record<Where, readonly [viewBox: string, fit: string]> = {
  wide: ['0 0 400 100', 'none'],
  start: ['0 0 150 100', 'xMinYMax meet'],
  mid: ['0 0 150 100', 'xMidYMax meet'],
  end: ['0 0 150 100', 'xMaxYMax meet'],
};

/** A layer of a scene, drawn past its own box where the banner has room (a rainbow's arc). */
function Layer({ at, children }: { at: Where; children: ReactNode }) {
  const [viewBox, fit] = PLACES[at];
  return (
    <svg width="100%" height="100%" viewBox={viewBox} preserveAspectRatio={fit} overflow="visible">
      {children}
    </svg>
  );
}

/** A line that keeps its width however a `wide` layer is stretched. */
const HAIR = { vectorEffect: 'non-scaling-stroke' as const, strokeLinecap: 'round' as const };

// ── Colors ─────────────────────────────────────────────────────────────────

function hsl(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.slice(1), 16);
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h =
    max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return [h / 6, s, l];
}

function hex(h: number, s: number, l: number): string {
  const f = (n: number) => {
    const k = (n + h * 12) % 12;
    const a = s * Math.min(l, 1 - l);
    const c = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(c * 255)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

/**
 * A team's color moved into the middle tones, its hue kept: a navy or a
 * near-white would vanish into a dark page or a light one at part strength.
 */
export function middle(color: string, lo = 0.4, hi = 0.62): string {
  const [h, s, l] = hsl(color);
  return hex(h, s, Math.min(hi, Math.max(lo, l)));
}

/** What a scene is drawn with: the team's colors in the middle tones, and an id for its gradients. */
interface Kit {
  readonly p: string;
  readonly s: string;
  readonly id: string;
}

const n = (v: number) => v.toFixed(1);

// ── Small drawings ─────────────────────────────────────────────────────────

const BAT =
  'M0 2 Q-3 -3 -9 -2 Q-12 2 -16 0 Q-13 6 -7 4 Q-4 8 0 4 Q4 8 7 4 Q13 6 16 0 Q12 2 9 -2 Q3 -3 0 2 Z';
const MAPLE =
  'M0 -10 L1.6 -6 L4.5 -7.5 L3.8 -3.2 L8.5 -4.5 L6.6 -1.2 L9.6 0.6 L5 2.2 L6 5 L1.8 3.6 L0.8 8 ' +
  'L0 6.8 L-0.8 8 L-1.8 3.6 L-6 5 L-5 2.2 L-9.6 0.6 L-6.6 -1.2 L-8.5 -4.5 L-3.8 -3.2 L-4.5 -7.5 L-1.6 -6 Z';
const LEAF = 'M0 -8 Q6 -3 0 8 Q-6 -3 0 -8 Z';
const PINE = 'M0 -30 L10 -15 L5 -15 L14 -2 L7 -2 L17 12 L-17 12 L-7 -2 L-14 -2 L-5 -15 L-10 -15 Z';
const PINE_SNOW =
  'M0 -30 L6 -21 L2 -22 L0 -19 L-3 -22 L-6 -21 Z M-5 -15 L-10 -15 L-8 -12 L-4 -13 Z';

/** A four-pointed sparkle around (x, y), `r` from its middle to a point. */
function sparkle(x: number, y: number, r: number): string {
  const w = r * 0.28;
  return (
    `M${n(x)} ${n(y - r)} Q${n(x + w)} ${n(y - w)} ${n(x + r)} ${n(y)} ` +
    `Q${n(x + w)} ${n(y + w)} ${n(x)} ${n(y + r)} Q${n(x - w)} ${n(y + w)} ${n(x - r)} ${n(y)} ` +
    `Q${n(x - w)} ${n(y - w)} ${n(x)} ${n(y - r)} Z`
  );
}

/** A circle as path commands, so many dots are one path. */
const dot = (x: number, y: number, r: number) =>
  `M${n(x - r)} ${n(y)}a${n(r)} ${n(r)} 0 1 0 ${n(2 * r)} 0a${n(r)} ${n(r)} 0 1 0 ${n(-2 * r)} 0Z`;

/** A snowflake's six arms, each with a pair of twigs, around (x, y). */
function flake(x: number, y: number, r: number): string {
  let d = '';
  for (let k = 0; k < 6; k++) {
    const a = (k * Math.PI) / 3 - Math.PI / 2;
    const ex = x + Math.cos(a) * r;
    const ey = y + Math.sin(a) * r;
    d += `M${n(x)} ${n(y)}L${n(ex)} ${n(ey)}`;
    const mx = x + Math.cos(a) * r * 0.58;
    const my = y + Math.sin(a) * r * 0.58;
    for (const t of [-0.6, 0.6]) {
      const b = a + t;
      d += `M${n(mx)} ${n(my)}L${n(mx + Math.cos(b) * r * 0.32)} ${n(my + Math.sin(b) * r * 0.32)}`;
    }
  }
  return d;
}

function Pumpkin({ x, y, r }: { x: number; y: number; r: number }) {
  return (
    <g transform={`translate(${x} ${y})`} opacity={0.8}>
      <rect
        x={-r * 0.12}
        y={-r * 1.15}
        width={r * 0.24}
        height={r * 0.45}
        rx={r * 0.08}
        fill="#2b8a3e"
      />
      <ellipse cx={-r * 0.48} cy={0} rx={r * 0.62} ry={r * 0.82} fill="#e8590c" />
      <ellipse cx={r * 0.48} cy={0} rx={r * 0.62} ry={r * 0.82} fill="#e8590c" />
      <ellipse cx={0} cy={0} rx={r * 0.62} ry={r * 0.88} fill="#fd7e14" />
    </g>
  );
}

/** A three-leafed clover around (x, y), its leaves `r` across. */
function Clover({
  x,
  y,
  r,
  color,
  opacity,
}: {
  x: number;
  y: number;
  r: number;
  color: string;
  opacity: number;
}) {
  const leaf = heart(0, -r * 0.42, r);
  return (
    <g transform={`translate(${x} ${y})`} opacity={opacity} fill={color}>
      <path
        d={`M0 0 Q${n(r * 0.15)} ${n(r * 0.5)} ${n(r * 0.35)} ${n(r * 0.85)}`}
        stroke={color}
        strokeWidth={r * 0.12}
        fill="none"
      />
      {[0, 120, 240].map((deg) => (
        <path key={deg} d={leaf} transform={`rotate(${deg})`} />
      ))}
    </g>
  );
}

/** A soft glow, brightest in the middle. */
function Glow({ id, color, strength }: { id: string; color: string; strength: number }) {
  return (
    <radialGradient id={id}>
      <stop offset="0" stopColor={color} stopOpacity={strength} />
      <stop offset="1" stopColor={color} stopOpacity={0} />
    </radialGradient>
  );
}

/** A sky from its top color to its bottom one, each at its own strength. */
function Sky({
  id,
  stops,
}: {
  id: string;
  stops: readonly (readonly [offset: number, color: string, opacity: number])[];
}) {
  return (
    <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
      {stops.map(([offset, color, opacity]) => (
        <stop key={offset} offset={offset} stopColor={color} stopOpacity={opacity} />
      ))}
    </linearGradient>
  );
}

// ── The scenes ─────────────────────────────────────────────────────────────

/** Confetti's pieces: where, turned how far, which shape and which color. */
const PIECES: readonly (readonly [
  x: number,
  y: number,
  deg: number,
  shape: 0 | 1 | 2,
  color: number,
])[] = [
  [138, 8, 20, 0, 0],
  [120, 16, 70, 1, 1],
  [104, 6, 140, 0, 2],
  [130, 30, 100, 2, 3],
  [92, 22, 40, 0, 4],
  [146, 46, 160, 0, 1],
  [112, 40, 10, 1, 0],
  [78, 12, 120, 2, 1],
  [84, 40, 60, 0, 3],
  [66, 26, 0, 1, 2],
  [140, 64, 80, 1, 4],
  [100, 56, 30, 0, 0],
  [58, 46, 150, 0, 1],
  [124, 54, 50, 2, 2],
  [70, 62, 90, 1, 3],
  [48, 18, 110, 0, 4],
];

function confettiPieces(k: Kit, pieces: typeof PIECES, scale: number): ReactNode[] {
  const colors = [k.p, k.s, '#fcc419', '#f06595', '#4dabf7'];
  return pieces.map(([x, y, deg, shape, c]) => {
    const fill = colors[c]!;
    const t = `translate(${x} ${y}) rotate(${deg}) scale(${scale})`;
    if (shape === 0)
      return (
        <rect
          key={`${x}-${y}`}
          x={-1.6}
          y={-3.6}
          width={3.2}
          height={7.2}
          rx={0.6}
          fill={fill}
          transform={t}
        />
      );
    if (shape === 1) return <circle key={`${x}-${y}`} r={1.9} fill={fill} transform={t} />;
    return (
      <path
        key={`${x}-${y}`}
        d="M-4 0 Q-2 -3 0 0 T4 0"
        stroke={fill}
        strokeWidth={1.4}
        fill="none"
        strokeLinecap="round"
        transform={t}
      />
    );
  });
}

type Draw = (k: Kit) => ReactNode;

const SCENES: Record<BannerScene, Draw> = {
  confetti: (k) => (
    <>
      <Layer at="end">
        <path
          d="M150 0 C140 10 152 18 142 28 C132 38 146 46 136 56"
          stroke={k.p}
          strokeWidth={2}
          fill="none"
          opacity={0.45}
        />
        <path
          d="M132 0 C124 8 134 14 126 22 C118 30 128 36 120 44"
          stroke={k.s}
          strokeWidth={1.6}
          fill="none"
          opacity={0.45}
        />
        <g opacity={0.75}>{confettiPieces(k, PIECES, 1)}</g>
      </Layer>
      <Layer at="mid">
        <g opacity={0.5}>{confettiPieces(k, PIECES.slice(0, 8), 0.8)}</g>
      </Layer>
      <Layer at="start">
        <g opacity={0.35}>{confettiPieces(k, PIECES.slice(8, 13), 0.7)}</g>
      </Layer>
    </>
  ),

  stars: (k) => (
    <>
      <defs>
        <Sky
          id={`${k.id}-sky`}
          stops={[
            [0, '#364fc7', 0.26],
            [1, '#748ffc', 0.04],
          ]}
        />
        <linearGradient id={`${k.id}-trail`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#fff3bf" stopOpacity={0} />
          <stop offset="1" stopColor="#fff3bf" stopOpacity={0.85} />
        </linearGradient>
      </defs>
      <Layer at="wide">
        <rect width={400} height={100} fill={`url(#${k.id}-sky)`} />
      </Layer>
      <Layer at="end">
        <path className="b-bright" d={sparkle(139, 13, 7)} fill="#ffd43b" opacity={0.9} />
        <path
          className="b-bright"
          d={[
            star(98, 8, 2.6, 1.1),
            star(148, 44, 2.4, 1),
            star(56, 30, 2, 0.9),
            star(30, 12, 2.2, 1),
          ].join('')}
          fill="#ffd43b"
          opacity={0.75}
        />
        <path
          d={[
            dot(64, 50, 0.9),
            dot(120, 16, 0.8),
            dot(44, 18, 0.7),
            dot(148, 70, 0.8),
            dot(20, 40, 0.7),
          ].join('')}
          fill="#fff3bf"
          opacity={0.8}
        />
        <path
          d="M34 2 L70 14"
          stroke={`url(#${k.id}-trail)`}
          strokeWidth={1.6}
          strokeLinecap="round"
        />
        <path className="b-bright" d={sparkle(71, 14.3, 3)} fill="#fff3bf" opacity={0.9} />
      </Layer>
      <Layer at="mid">
        <path
          d={[star(40, 20, 2, 0.9), dot(70, 12, 0.8), dot(110, 30, 0.7), dot(20, 44, 0.7)].join('')}
          fill="#ffd43b"
          opacity={0.55}
        />
      </Layer>
      <Layer at="start">
        <path
          d={[dot(30, 16, 0.8), dot(64, 30, 0.7), dot(100, 12, 0.8)].join('')}
          fill="#ffd43b"
          opacity={0.4}
        />
      </Layer>
    </>
  ),

  stripes: (k) => {
    const band = (x: number, w: number) =>
      `M${x} 100 L${x + 42} 0 L${x + 42 + w} 0 L${x + w} 100 Z`;
    return (
      <>
        <defs>
          <linearGradient id={`${k.id}-wash`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0.4" stopColor={k.p} stopOpacity={0} />
            <stop offset="1" stopColor={k.p} stopOpacity={0.14} />
          </linearGradient>
        </defs>
        <Layer at="wide">
          <rect width={400} height={100} fill={`url(#${k.id}-wash)`} />
        </Layer>
        <Layer at="end">
          <path d={band(30, 18) + band(76, 18)} fill={k.p} opacity={0.36} />
          <path d={band(54, 9)} fill={k.s} opacity={0.45} />
          <path d={band(100, 3)} fill={k.s} opacity={0.4} />
        </Layer>
      </>
    );
  },

  sunburst: (k) => {
    const [ox, oy] = [115, 104];
    let rays = '';
    for (let i = 0; i < 24; i += 2) {
      const a = Math.PI + (i * Math.PI) / 24;
      const b = Math.PI + ((i + 1) * Math.PI) / 24;
      rays += `M${ox} ${oy}L${n(ox + Math.cos(a) * 420)} ${n(oy + Math.sin(a) * 420)}L${n(ox + Math.cos(b) * 420)} ${n(oy + Math.sin(b) * 420)}Z`;
    }
    return (
      <>
        <defs>
          <Glow id={`${k.id}-glow`} color={k.s} strength={0.35} />
          <radialGradient
            id={`${k.id}-fade`}
            cx={ox}
            cy={oy}
            r={260}
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0" stopColor={k.p} stopOpacity={0.22} />
            <stop offset="1" stopColor={k.p} stopOpacity={0.02} />
          </radialGradient>
        </defs>
        <Layer at="end">
          <path d={rays} fill={`url(#${k.id}-fade)`} />
          <circle cx={ox} cy={oy} r={64} fill={`url(#${k.id}-glow)`} />
        </Layer>
      </>
    );
  },

  dots: (k) => {
    // HALFTONE: dots that shrink away from the banner's lower end.
    let d = '';
    const reach = 150;
    for (let y = 4; y <= 100; y += 8)
      for (let x = 146; x >= -10; x -= 8) {
        const off = ((y / 8) % 2) * 4;
        const dist = Math.hypot(150 - (x - off), 104 - y);
        const r = 3 * (1 - dist / reach);
        if (r > 0.45) d += dot(x - off, y, r);
      }
    return (
      <Layer at="end">
        <path d={d} fill={k.p} opacity={0.34} />
      </Layer>
    );
  },

  checks: (k) => {
    // A CHECKERED FLAG on its pole, waving, and the air it moves through.
    const cols = 6;
    const rows = 4;
    const size = 7;
    const [x0, y0] = [37, 8];
    const wave = (c: number) => Math.sin(c * 0.95) * 3;
    let dark = '';
    let light = '';
    for (let c = 0; c < cols; c++)
      for (let r = 0; r < rows; r++) {
        const xa = x0 + c * size;
        const xb = xa + size;
        const ya = y0 + r * size;
        const yb = ya + size;
        const cell = `M${n(xa)} ${n(ya + wave(c))}L${n(xb)} ${n(ya + wave(c + 1))}L${n(xb)} ${n(yb + wave(c + 1))}L${n(xa)} ${n(yb + wave(c))}Z`;
        if ((c + r) % 2 === 0) dark += cell;
        else light += cell;
      }
    // THE FINISH LINE along the bottom, two rows of squares that fade away
    // from the banner's end.
    let line = '';
    for (let x = 150 - 6; x >= -250; x -= 6)
      for (const y of [88, 94])
        if (((150 - x) / 6 + (y === 94 ? 1 : 0)) % 2 === 0) line += `M${x} ${y}h6v6h-6Z`;
    return (
      <>
        <defs>
          <linearGradient id={`${k.id}-wash`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0.35" stopColor={k.p} stopOpacity={0} />
            <stop offset="1" stopColor={k.p} stopOpacity={0.14} />
          </linearGradient>
          <linearGradient
            id={`${k.id}-line`}
            x1={-250}
            y1={0}
            x2={150}
            y2={0}
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0" stopColor="#495057" stopOpacity={0} />
            <stop offset="1" stopColor="#495057" stopOpacity={0.45} />
          </linearGradient>
        </defs>
        <Layer at="wide">
          <rect width={400} height={100} fill={`url(#${k.id}-wash)`} />
        </Layer>
        <Layer at="end">
          <path d={line} fill={`url(#${k.id}-line)`} />
          <path
            d="M36 100 L36 6"
            stroke="#868e96"
            strokeWidth={2}
            opacity={0.55}
            strokeLinecap="round"
          />
          <path d={dark} fill="#343a40" opacity={0.42} />
          <path d={light} fill="#f1f3f5" opacity={0.42} />
        </Layer>
      </>
    );
  },

  field: (k) => {
    let lines = '';
    for (let x = 20; x <= 400; x += 46) lines += `M${x} 70 L${x - 14} 100`;
    return (
      <>
        <defs>
          <Sky
            id={`${k.id}-sky`}
            stops={[
              [0, '#a5d8ff', 0.2],
              [0.7, '#a5d8ff', 0],
            ]}
          />
          <Sky
            id={`${k.id}-turf`}
            stops={[
              [0, '#51cf66', 0.3],
              [1, '#2b8a3e', 0.42],
            ]}
          />
        </defs>
        <Layer at="wide">
          <rect width={400} height={100} fill={`url(#${k.id}-sky)`} />
          <rect y={70} width={400} height={30} fill={`url(#${k.id}-turf)`} />
          <path d={lines} stroke="#ffffff" strokeWidth={1.5} opacity={0.55} {...HAIR} />
          <path d="M0 70 L400 70" stroke="#ffffff" strokeWidth={1.5} opacity={0.5} {...HAIR} />
        </Layer>
        <Layer at="end">
          <g stroke="#fab005" strokeLinecap="round" fill="none" opacity={0.8}>
            <path d="M118 74 L118 60" strokeWidth={3} />
            <path d="M98 60 L138 60" strokeWidth={3} />
            <path d="M98 60 L98 18 M138 60 L138 18" strokeWidth={2.4} />
          </g>
          <g transform="translate(70 28) rotate(-28)" opacity={0.6}>
            <ellipse rx={7} ry={4.2} fill="#8b4a1c" />
            <path
              d="M-2.6 0 L2.6 0 M-1.4 -1 L-1.4 1 M0 -1 L0 1 M1.4 -1 L1.4 1"
              stroke="#ffffff"
              strokeWidth={0.6}
            />
          </g>
        </Layer>
      </>
    );
  },

  lights: (k) => {
    // ONE TOWER AT THE EDGE, its lamps above the character's shoulder and its
    // beam across the field; a second at the start sat behind the team's name.
    const tower = (x: number, flip: 1 | -1, strength: number) => (
      <g opacity={strength}>
        <path
          d={`M${x - 16} 28 L${x + 16} 28 L${x - flip * 30} 100 L${x - flip * 120} 100 Z`}
          fill={`url(#${k.id}-beam)`}
        />
        <path d={`M${x} 100 L${x} 30`} stroke="#495057" strokeWidth={3} opacity={0.6} />
        <rect x={x - 18} y={13} width={36} height={16} rx={2} fill="#495057" opacity={0.6} />
        <path
          className="b-bright"
          d={[0, 1, 2, 3]
            .flatMap((i) => [dot(x - 13.5 + i * 9, 18.2, 2.6), dot(x - 13.5 + i * 9, 25, 2.6)])
            .join('')}
          fill="#fff9db"
          opacity={0.95}
        />
        <circle className="b-bright" cx={x} cy={21} r={26} fill={`url(#${k.id}-glow)`} />
      </g>
    );
    return (
      <>
        <defs>
          <Sky
            id={`${k.id}-sky`}
            stops={[
              [0, '#364fc7', 0.2],
              [1, '#364fc7', 0],
            ]}
          />
          <Sky
            id={`${k.id}-beam`}
            stops={[
              [0, '#fff3bf', 0.38],
              [1, '#fff3bf', 0],
            ]}
          />
          <Glow id={`${k.id}-glow`} color="#fff3bf" strength={0.5} />
        </defs>
        <Layer at="wide">
          <rect width={400} height={100} fill={`url(#${k.id}-sky)`} />
          <path d="M0 72 Q200 88 400 72 V100 H0 Z" fill="#495057" opacity={0.2} />
          <rect y={93} width={400} height={7} fill="#2b8a3e" opacity={0.3} />
        </Layer>
        <Layer at="end">{tower(140, 1, 1)}</Layer>
      </>
    );
  },

  skyline: (k) => {
    // The far city, low blocks across the whole width.
    const far: string[] = [];
    let x = 0;
    for (let i = 0; x < 400; i++) {
      const w = 10 + ((i * 7) % 13);
      const h = 9 + ((i * 11) % 15);
      far.push(`M${x} 100 L${x} ${100 - h} L${x + w} ${100 - h} L${x + w} 100 Z`);
      x += w + 1;
    }
    const towers: readonly (readonly [x: number, w: number, top: number])[] = [
      [62, 14, 56],
      [78, 11, 40],
      [91, 17, 60],
      [110, 12, 30],
      [124, 18, 48],
      [144, 10, 64],
    ];
    let windows = '';
    for (const [tx, w, top] of towers)
      for (let wy = top + 5; wy < 96; wy += 7)
        for (let wx = tx + 3; wx < tx + w - 3; wx += 5)
          if ((wx * 3 + wy * 7) % 5 < 2) windows += `M${wx} ${wy}h2v2.6h-2Z`;
    return (
      <>
        <defs>
          <Sky
            id={`${k.id}-sky`}
            stops={[
              [0, '#4263eb', 0.18],
              [1, '#ffa94d', 0.16],
            ]}
          />
        </defs>
        <Layer at="wide">
          <rect width={400} height={100} fill={`url(#${k.id}-sky)`} />
          <path d={far.join('')} fill="#5c7cfa" opacity={0.18} />
        </Layer>
        <Layer at="end">
          <path
            d={towers
              .map(([tx, w, top]) => `M${tx} 100 L${tx} ${top} L${tx + w} ${top} L${tx + w} 100 Z`)
              .join('')}
            fill="#3b5bdb"
            opacity={0.32}
          />
          <path d="M116 30 L116 20" stroke="#3b5bdb" strokeWidth={1.2} opacity={0.4} />
          <path d={windows} fill="#ffe066" opacity={0.65} />
        </Layer>
        <Layer at="start">
          <path
            d="M4 100 L4 70 L16 70 L16 100 Z M18 100 L18 62 L28 62 L28 100 Z M30 100 L30 76 L44 76 L44 100 Z"
            fill="#3b5bdb"
            opacity={0.18}
          />
        </Layer>
      </>
    );
  },

  mountains: (k) => (
    <>
      <defs>
        <Sky
          id={`${k.id}-sky`}
          stops={[
            [0, '#74c0fc', 0.22],
            [0.8, '#74c0fc', 0],
          ]}
        />
        <Glow id={`${k.id}-sun`} color="#ffd43b" strength={0.4} />
      </defs>
      <Layer at="wide">
        <rect width={400} height={100} fill={`url(#${k.id}-sky)`} />
        <path
          d="M0 66 L30 52 L62 62 L104 34 L140 56 L178 42 L220 62 L262 38 L300 58 L344 44 L400 60 V100 H0 Z"
          fill="#748ffc"
          opacity={0.22}
        />
        <path
          d="M96 39 L104 34 L112 39 L107 38 L104 41 L100 38 Z M254 43 L262 38 L270 43 L265 42 L262 45 L258 42 Z"
          fill="#ffffff"
          opacity={0.6}
        />
        <path
          d="M0 82 L46 64 L86 78 L134 58 L182 80 L236 62 L288 80 L340 66 L400 78 V100 H0 Z"
          fill="#4263eb"
          opacity={0.22}
        />
        <path d="M0 94 Q100 86 200 92 T400 90 V100 H0 Z" fill="#2f9e44" opacity={0.26} />
      </Layer>
      <Layer at="end">
        <circle className="b-bright" cx={142} cy={16} r={20} fill={`url(#${k.id}-sun)`} />
        <circle className="b-bright" cx={142} cy={16} r={8} fill="#ffd43b" opacity={0.6} />
        <path
          d="M44 14 Q47 11 50 14 Q53 11 56 14 M58 22 Q60.5 19.5 63 22 Q65.5 19.5 68 22 M32 24 Q34 22 36 24 Q38 22 40 24"
          stroke="#495057"
          strokeWidth={1.1}
          fill="none"
          opacity={0.5}
          strokeLinecap="round"
        />
      </Layer>
    </>
  ),

  sunset: (k) => {
    let shimmer = '';
    for (const [x, y, w] of [
      [30, 80, 18],
      [90, 86, 24],
      [170, 78, 16],
      [240, 90, 22],
      [320, 82, 18],
      [370, 92, 14],
    ] as const)
      shimmer += `M${x} ${y} L${x + w} ${y}`;
    return (
      <>
        <defs>
          <Sky
            id={`${k.id}-sky`}
            stops={[
              [0, '#f783ac', 0.18],
              [0.55, '#ffa94d', 0.24],
              [0.72, '#ffd43b', 0.2],
            ]}
          />
          <Sky
            id={`${k.id}-sea`}
            stops={[
              [0, '#339af0', 0.3],
              [1, '#1c7ed6', 0.42],
            ]}
          />
        </defs>
        <Layer at="wide">
          <rect width={400} height={72} fill={`url(#${k.id}-sky)`} />
        </Layer>
        <Layer at="end">
          <circle className="b-bright" cx={50} cy={72} r={16} fill="#ffd43b" opacity={0.75} />
        </Layer>
        <Layer at="wide">
          <rect y={72} width={400} height={28} fill={`url(#${k.id}-sea)`} />
          <path d={shimmer} stroke="#ffffff" strokeWidth={1.2} opacity={0.45} {...HAIR} />
        </Layer>
        <Layer at="end">
          <path
            d="M36 77 L64 77 M40 82 L60 82 M44 87 L56 87 M47 92 L53 92"
            stroke="#ffe066"
            strokeWidth={1.4}
            opacity={0.6}
            strokeLinecap="round"
          />
          <g opacity={0.55}>
            <path
              d="M144 100 Q140 72 130 52"
              stroke="#5c3d2e"
              strokeWidth={3.2}
              fill="none"
              strokeLinecap="round"
            />
            <path
              d="M130 52 Q114 42 100 50 Q116 46 130 52 Z M130 52 Q120 34 106 32 Q120 40 130 52 Z M130 52 Q134 34 148 30 Q138 40 130 52 Z M130 52 Q148 44 156 54 Q144 48 130 52 Z M130 52 Q126 60 116 66 Q124 58 130 52 Z"
              fill="#2b8a3e"
            />
          </g>
        </Layer>
      </>
    );
  },

  spotlight: (k) => (
    <>
      <defs>
        <Sky
          id={`${k.id}-beam`}
          stops={[
            [0, '#ffd43b', 0.42],
            [1, '#ffe066', 0.1],
          ]}
        />
        <Glow id={`${k.id}-pool`} color="#ffd43b" strength={0.6} />
      </defs>
      <Layer at="wide">
        <rect width={400} height={100} fill="#212529" opacity={0.07} />
      </Layer>
      <Layer at="end">
        <path d="M106 0 L124 0 L154 96 L76 96 Z" fill={`url(#${k.id}-beam)`} />
        <ellipse cx={115} cy={95} rx={42} ry={6} fill={`url(#${k.id}-pool)`} />
        <path
          className="b-bright"
          d={[sparkle(58, 26, 4), sparkle(148, 28, 3.2), sparkle(48, 62, 2.6)].join('')}
          fill="#fcc419"
          opacity={0.75}
        />
      </Layer>
    </>
  ),

  bats: (k) => (
    <>
      <defs>
        <Sky
          id={`${k.id}-sky`}
          stops={[
            [0, '#5b3a8c', 0.32],
            [0.65, '#c0508a', 0.13],
            [1, '#ff8a3d', 0.22],
          ]}
        />
        <Glow id={`${k.id}-glow`} color="#fff3c4" strength={0.55} />
      </defs>
      <Layer at="wide">
        <rect width={400} height={100} fill={`url(#${k.id}-sky)`} />
        <path
          d="M0 80 Q50 68 110 76 T230 72 T340 70 T400 74 V100 H0 Z"
          fill="#4a2f6e"
          opacity={0.28}
        />
        <path d="M0 90 Q70 80 160 88 T300 86 T400 88 V100 H0 Z" fill="#2e1d47" opacity={0.38} />
      </Layer>
      <Layer at="end">
        <circle className="b-bright" cx={134} cy={16} r={28} fill={`url(#${k.id}-glow)`} />
        <g className="b-bright">
          <circle cx={134} cy={16} r={12} fill="#fff1bf" opacity={0.85} />
          <path
            d={[dot(130, 13, 2), dot(138, 19, 1.5), dot(136, 11, 1)].join('')}
            fill="#e9d89a"
            opacity={0.6}
          />
        </g>
        <g fill="#2b1d3d" opacity={0.72}>
          <path d={BAT} transform="translate(108 10) scale(0.8)" />
          <path d={BAT} transform="translate(62 22) scale(0.6)" />
          <path d={BAT} transform="translate(84 34) scale(0.45)" />
        </g>
        <Pumpkin x={62} y={90} r={7} />
        <Pumpkin x={76} y={93} r={4.6} />
      </Layer>
      <Layer at="mid">
        <path d={BAT} transform="translate(60 22) scale(0.42)" fill="#2b1d3d" opacity={0.45} />
      </Layer>
    </>
  ),

  leaves: (k) => {
    const falling: readonly (readonly [
      x: number,
      y: number,
      deg: number,
      kind: 0 | 1,
      color: string,
      scale: number,
    ])[] = [
      [60, 40, 20, 0, '#d9480f', 1],
      [54, 64, -30, 1, '#f08c00', 1.1],
      [148, 56, 60, 0, '#c92a2a', 0.9],
      [40, 70, 110, 1, '#e67700', 0.9],
      [138, 78, -50, 0, '#f59f00', 1],
      [74, 22, 160, 0, '#e8590c', 0.8],
    ];
    const leafAt = ([x, y, deg, kind, color, scale]: (typeof falling)[number], key: string) => (
      <path
        key={key}
        d={kind === 0 ? MAPLE : LEAF}
        transform={`translate(${x} ${y}) rotate(${deg}) scale(${scale})`}
        fill={color}
      />
    );
    return (
      <>
        <defs>
          <Sky
            id={`${k.id}-sky`}
            stops={[
              [0, '#ffa94d', 0.2],
              [1, '#ffd8a8', 0.06],
            ]}
          />
        </defs>
        <Layer at="wide">
          <rect width={400} height={100} fill={`url(#${k.id}-sky)`} />
          <path d="M0 86 Q80 78 170 84 T330 82 T400 84 V100 H0 Z" fill="#a0522d" opacity={0.26} />
        </Layer>
        <Layer at="end">
          <path
            d="M152 4 Q126 12 104 7 Q92 5 80 12"
            stroke="#6b3e26"
            strokeWidth={2.4}
            fill="none"
            opacity={0.5}
            strokeLinecap="round"
          />
          <g opacity={0.8}>
            {leafAt([110, 14, 200, 0, '#e8590c', 0.85], 'b1')}
            {leafAt([132, 13, 160, 0, '#f08c00', 0.75], 'b2')}
            {leafAt([90, 13, 190, 0, '#c92a2a', 0.7], 'b3')}
          </g>
          <g opacity={0.72}>{falling.map((f, i) => leafAt(f, `f${i}`))}</g>
        </Layer>
        <Layer at="mid">
          <g opacity={0.45}>{falling.slice(0, 3).map((f, i) => leafAt(f, `m${i}`))}</g>
        </Layer>
        <Layer at="start">
          <g opacity={0.3}>{falling.slice(3, 5).map((f, i) => leafAt(f, `s${i}`))}</g>
        </Layer>
      </>
    );
  },

  snow: (k) => (
    <>
      <defs>
        <Sky
          id={`${k.id}-sky`}
          stops={[
            [0, '#5c7cfa', 0.22],
            [1, '#a5d8ff', 0.08],
          ]}
        />
      </defs>
      <Layer at="wide">
        <rect width={400} height={100} fill={`url(#${k.id}-sky)`} />
        <path d="M0 76 Q100 62 210 74 T400 70 V100 H0 Z" fill="#91a7ff" opacity={0.3} />
        <path d="M0 88 Q120 78 240 88 T400 86 V100 H0 Z" fill="#f8f9fa" opacity={0.6} />
      </Layer>
      <Layer at="end">
        <g opacity={0.6}>
          <path d={PINE} transform="translate(126 82) scale(1.25)" fill="#2b8a3e" />
          <path d={PINE} transform="translate(146 88) scale(0.85)" fill="#2f9e44" />
        </g>
        <g fill="#f8f9fa" opacity={0.85}>
          <path d={PINE_SNOW} transform="translate(126 82) scale(1.25)" />
          <path d={PINE_SNOW} transform="translate(146 88) scale(0.85)" />
        </g>
        <path
          d={[
            flake(66, 18, 6),
            flake(52, 44, 4),
            flake(132, 14, 5),
            flake(30, 30, 3.4),
            flake(100, 8, 3),
          ].join('')}
          stroke="#74c0fc"
          strokeWidth={1.1}
          fill="none"
          opacity={0.8}
          strokeLinecap="round"
        />
      </Layer>
      <Layer at="mid">
        <path
          d={[flake(40, 22, 3.4), flake(100, 36, 2.6), flake(70, 54, 2.2)].join('')}
          stroke="#74c0fc"
          strokeWidth={1}
          fill="none"
          opacity={0.5}
          strokeLinecap="round"
        />
      </Layer>
      <Layer at="start">
        <path
          d={[flake(30, 24, 2.8), flake(80, 40, 2.2)].join('')}
          stroke="#74c0fc"
          strokeWidth={1}
          fill="none"
          opacity={0.35}
          strokeLinecap="round"
        />
      </Layer>
    </>
  ),

  hearts: (k) => (
    <>
      <defs>
        <Sky
          id={`${k.id}-sky`}
          stops={[
            [0, '#f783ac', 0.2],
            [1, '#ffdeeb', 0.06],
          ]}
        />
      </defs>
      <Layer at="wide">
        <rect width={400} height={100} fill={`url(#${k.id}-sky)`} />
      </Layer>
      <Layer at="end">
        <path d={heart(138, 18, 20)} fill="#e64980" opacity={0.55} />
        <path d={heart(62, 22, 14) + heart(148, 56, 12)} fill="#f06595" opacity={0.45} />
        <path
          d={heart(52, 66, 10) + heart(96, 8, 8) + heart(36, 40, 8)}
          fill="#ff8fab"
          opacity={0.45}
        />
        <path
          className="b-bright"
          d={[sparkle(116, 6, 3), sparkle(70, 46, 2.4)].join('')}
          fill="#fcc2d7"
          opacity={0.8}
        />
      </Layer>
      <Layer at="mid">
        <path d={heart(60, 30, 10) + heart(96, 66, 7)} fill="#f06595" opacity={0.3} />
      </Layer>
      <Layer at="start">
        <path d={heart(22, 24, 8)} fill="#f06595" opacity={0.22} />
      </Layer>
    </>
  ),

  clovers: (k) => {
    const arcs = ['#fa5252', '#fd7e14', '#fcc419', '#40c057', '#339af0', '#845ef7'];
    return (
      <>
        <defs>
          <Sky
            id={`${k.id}-sky`}
            stops={[
              [0, '#8ce99a', 0.16],
              [0.8, '#8ce99a', 0],
            ]}
          />
        </defs>
        <Layer at="wide">
          <rect width={400} height={100} fill={`url(#${k.id}-sky)`} />
          <path d="M0 78 Q90 64 190 74 T400 70 V100 H0 Z" fill="#69db7c" opacity={0.3} />
        </Layer>
        <Layer at="end">
          <g fill="none" opacity={0.32}>
            {arcs.map((c, i) => {
              const r = 66 - i * 6.5;
              return (
                <path
                  key={c}
                  d={`M${n(118 - r)} 100 A${r} ${r} 0 0 1 ${n(118 + r)} 100`}
                  stroke={c}
                  strokeWidth={6.5}
                />
              );
            })}
          </g>
        </Layer>
        <Layer at="wide">
          <path d="M0 90 Q110 80 220 88 T400 86 V100 H0 Z" fill="#2f9e44" opacity={0.36} />
        </Layer>
        <Layer at="end">
          <g opacity={0.8}>
            <path
              d="M44 86 Q44 80 50 80 L64 80 Q70 80 70 86 Q70 96 57 96 Q44 96 44 86 Z"
              fill="#343a40"
            />
            <path
              d={[dot(51, 80, 2.4), dot(56, 78.5, 2.4), dot(61, 79.5, 2.4), dot(66, 81, 2)].join(
                '',
              )}
              fill="#fcc419"
            />
          </g>
          <Clover x={146} y={18} r={6} color="#2b8a3e" opacity={0.6} />
          <Clover x={64} y={20} r={5} color="#2f9e44" opacity={0.5} />
          <Clover x={36} y={50} r={4} color="#2b8a3e" opacity={0.45} />
        </Layer>
        <Layer at="mid">
          <Clover x={40} y={30} r={4.4} color="#2f9e44" opacity={0.35} />
        </Layer>
      </>
    );
  },
};

// ── The banner ─────────────────────────────────────────────────────────────

/** A scene drawn in a team's colors: the banner itself, and each tile of the picker. */
export function BannerArt({
  scene,
  colors,
  mirror = false,
  className,
}: {
  scene: BannerScene;
  colors: TeamColors;
  /** Its end at the start: a matchup's other side, so the two scenes meet in the middle. */
  mirror?: boolean;
  className?: string;
}) {
  const id = useDrawingId();
  const kit: Kit = { p: middle(colors.primary), s: middle(colors.secondary), id };
  return (
    <svg
      className={`fsp-banner${mirror ? ' is-mirrored' : ''}${className ? ` ${className}` : ''}`}
      data-banner={scene}
      // DRAWN BEHIND THE WORDS ON PURPOSE: the ground a team's name and score
      // sit on, which `qa:ux`'s UX-10 reads as ground rather than as an icon
      // laid over them.
      data-backdrop=""
      width="100%"
      height="100%"
      aria-hidden
      focusable="false"
    >
      {SCENES[scene](kit)}
    </svg>
  );
}

/** What a team needs for its banner to be drawn: the shape every `TeamRef` has. */
export interface BannerTeam {
  colors: TeamColors;
  badge?: TeamBadge;
  banner?: TeamBannerId | null;
}

/**
 * A team's banner behind whatever its box holds, the scene it shows today
 * (`bannerSceneOn`), or nothing for a team with none. The box is the
 * caller's: it takes the class `fsp-has-banner`, which puts the banner under
 * its contents.
 */
export function TeamBanner({ team, mirror }: { team: BannerTeam; mirror?: boolean }) {
  const scene = bannerSceneOn(team, new Date());
  return scene === null ? null : <BannerArt scene={scene} colors={team.colors} mirror={mirror} />;
}

/** Whether a team shows a banner today: its box takes `fsp-has-banner` only then. */
export function hasBanner(team: BannerTeam): boolean {
  return bannerSceneOn(team, new Date()) !== null;
}
